import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api.js'
import { ago, dateTime, money } from '../../../lib/format.js'
import { Card, Drawer, DrawerSection, Empty, KeyValue, Notice, PageHead, Pager, RowsSkeleton, SearchInput } from '../../../components/ui.js'
import { toast } from '../../../components/feedback.js'
import { ClockIcon, DownloadIcon } from '../../../components/icons.js'
import { StatusBadge, downloadCsv, errorMessage } from './shared.js'

const PER_PAGE = 25

interface Notification {
  id: string
  channel: 'email' | 'whatsapp'
  isFallback: boolean
  recipient: string
  balance: string
  threshold: string | null
  currency: string
  status: string
  scheduledAt: string
  sentAt: string | null
  providerMessageId: string | null
  retryCount: number
  error: string | null
  createdAt: string
  rule: { id: string; name: string } | null
  merchant: { id: string; name: string } | null
  template: { id: string; name: string } | null
}

interface NotificationDetail extends Notification {
  rendered: Record<string, unknown> | null
  providerResponse: unknown
  idempotencyKey: string
  runId: string | null
}

const STATUSES = ['queued', 'processing', 'sent', 'failed', 'skipped', 'suppressed', 'cancelled']

/**
 * The delivery log, for one rule (embedded in its detail page) or everyone.
 *
 * "Sent" means the provider accepted the message. There is no Delivered or
 * Read here because Wakit and SMTP send no receipts back to this platform.
 */
export function NotificationTable({ ruleId, compact }: { ruleId?: string; compact?: boolean }): React.ReactNode {
  const [params] = useSearchParams()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(params.get('status') ?? '')
  const [channel, setChannel] = useState('')
  const [rule, setRule] = useState(ruleId ?? params.get('ruleId') ?? '')
  const [merchantId, setMerchantId] = useState(params.get('merchantId') ?? '')
  const [templateId, setTemplateId] = useState('')
  const [minThreshold, setMinThreshold] = useState('')
  const [maxThreshold, setMaxThreshold] = useState('')
  const [errorText, setErrorText] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState<string | null>(null)

  const query = {
    q: search,
    status,
    channel,
    ruleId: ruleId ?? rule,
    merchantId,
    templateId,
    minThreshold,
    maxThreshold,
    error: errorText,
    from,
    to: to ? `${to}T23:59:59` : '',
  }

  const list = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'notifications', query, page, compact],
    queryFn: () =>
      api<{ items: Notification[]; total: number; byStatus: Record<string, number> }>('/v1/admin/wallet-alerts/notifications', {
        query: { ...query, page, perPage: compact ? 8 : PER_PAGE },
      }),
    refetchInterval: 30_000,
  })

  const rules = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'rules', 'all-names'],
    queryFn: () => api<{ items: Array<{ id: string; name: string }> }>('/v1/admin/wallet-alerts/rules', { query: { perPage: 200, sort: 'name', dir: 'asc' } }),
    enabled: !ruleId && !compact,
  })
  const templates = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'templates'],
    queryFn: () => api<{ items: Array<{ id: string; name: string; channel: string }> }>('/v1/admin/wallet-alerts/templates'),
    enabled: !compact,
  })

  const exportCsv = useMutation({
    mutationFn: () => api<{ filename: string; csv: string }>('/v1/admin/wallet-alerts/notifications-export', { query }),
    onSuccess: (result) => {
      downloadCsv(result.filename, result.csv)
      toast('ok', 'Export ready', result.filename)
    },
    onError: (error) => toast('error', 'Export failed', errorMessage(error)),
  })

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value)
    setPage(1)
  }

  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0

  return (
    <>
      {!compact && (
        <div className="toolbar">
          <SearchInput value={search} onChange={reset(setSearch)} placeholder="Merchant, recipient or ID" />
          <select className="select" aria-label="Status" value={status} onChange={(e) => reset(setStatus)(e.target.value)}>
            <option value="">Status: All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
                {list.data?.byStatus[s] !== undefined ? ` (${list.data.byStatus[s]})` : ''}
              </option>
            ))}
          </select>
          <select className="select" aria-label="Channel" value={channel} onChange={(e) => reset(setChannel)(e.target.value)}>
            <option value="">Channel: All</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          {!ruleId && (
            <select className="select" aria-label="Rule" value={rule} onChange={(e) => reset(setRule)(e.target.value)}>
              <option value="">Rule: All</option>
              {(rules.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          <select className="select" aria-label="Template" value={templateId} onChange={(e) => reset(setTemplateId)(e.target.value)}>
            <option value="">Template: All</option>
            {(templates.data?.items ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <input className="input" style={{ width: 130, minWidth: 0 }} placeholder="Merchant ID" value={merchantId} onChange={(e) => reset(setMerchantId)(e.target.value.replace(/\D+/g, ''))} />
          <input className="input" style={{ width: 110, minWidth: 0 }} type="number" placeholder="Min threshold" value={minThreshold} onChange={(e) => reset(setMinThreshold)(e.target.value)} />
          <input className="input" style={{ width: 110, minWidth: 0 }} type="number" placeholder="Max threshold" value={maxThreshold} onChange={(e) => reset(setMaxThreshold)(e.target.value)} />
          <input className="input" style={{ width: 160, minWidth: 0 }} placeholder="Provider error contains" value={errorText} onChange={(e) => reset(setErrorText)(e.target.value)} />
          <input className="input" type="date" aria-label="From" value={from} onChange={(e) => reset(setFrom)(e.target.value)} />
          <input className="input" type="date" aria-label="To" value={to} onChange={(e) => reset(setTo)(e.target.value)} />
          <button type="button" className="btn btn--sm" disabled={exportCsv.isPending || total === 0} onClick={() => exportCsv.mutate()}>
            <DownloadIcon size={14} /> Export CSV
          </button>
        </div>
      )}

      {list.error && (
        <Empty error title="Could not load notifications">
          {errorMessage(list.error)}
        </Empty>
      )}
      {list.isPending && <RowsSkeleton rows={compact ? 3 : 8} />}
      {list.data && items.length === 0 && <Empty icon={<ClockIcon size={24} />} title="No notifications yet" />}

      {items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Merchant</th>
                {!ruleId && <th>Alert rule</th>}
                <th className="right">Balance at trigger</th>
                <th className="right">Threshold</th>
                <th>Channel / recipient</th>
                {!compact && <th>Template</th>}
                <th>Scheduled</th>
                <th>Sent</th>
                <th>Status</th>
                {!compact && <th>Provider message</th>}
                {!compact && <th className="right">Retries</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="is-clickable" onClick={() => setOpenId(n.id)}>
                  <td className="num">#{n.id}</td>
                  <td>
                    {n.merchant ? n.merchant.name : <span className="faint">deleted</span>}
                    {n.merchant && <span className="table__sub">#{n.merchant.id}</span>}
                  </td>
                  {!ruleId && <td>{n.rule?.name ?? <span className="faint">deleted rule</span>}</td>}
                  <td className="right num">{money(n.balance, n.currency)}</td>
                  <td className="right num">{n.threshold ? money(n.threshold, n.currency) : '—'}</td>
                  <td>
                    {n.channel === 'email' ? 'Email' : 'WhatsApp'}
                    {n.isFallback && ' (fallback)'}
                    <span className="table__sub">{n.recipient || '—'}</span>
                  </td>
                  {!compact && <td>{n.template?.name ?? '—'}</td>}
                  <td title={dateTime(n.scheduledAt)}>{ago(n.scheduledAt)}</td>
                  <td>{n.sentAt ? dateTime(n.sentAt) : '—'}</td>
                  <td>
                    <StatusBadge status={n.status} />
                    {n.error && <span className="table__sub" style={{ maxWidth: 220, whiteSpace: 'normal' }}>{n.error}</span>}
                  </td>
                  {!compact && <td className="num" style={{ fontSize: '.78rem' }}>{n.providerMessageId ?? '—'}</td>}
                  {!compact && <td className="right num">{n.retryCount}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!compact && total > PER_PAGE && (
        <Pager page={page} pageCount={Math.max(1, Math.ceil(total / PER_PAGE))} total={total} perPage={PER_PAGE} onPage={setPage} noun="notifications" />
      )}

      {openId && <NotificationDrawer id={openId} onClose={() => setOpenId(null)} />}
    </>
  )
}

function NotificationDrawer({ id, onClose }: { id: string; onClose: () => void }): React.ReactNode {
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'notification', id],
    queryFn: () => api<NotificationDetail>(`/v1/admin/wallet-alerts/notifications/${id}`),
  })

  const retry = useMutation({
    mutationFn: () => api(`/v1/admin/wallet-alerts/notifications/${id}/retry`, { method: 'POST' }),
    onSuccess: () => {
      toast('ok', 'Queued for another attempt')
      void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })
    },
    onError: (error) => toast('error', 'Could not retry', errorMessage(error)),
  })

  const n = detail.data
  const rendered = n?.rendered ?? null

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Notification #${id}`}
      subtitle={n ? `${n.channel === 'email' ? 'Email' : 'WhatsApp'} · ${n.status}` : undefined}
      footer={
        n && (
          <>
            {n.merchant && (
              <Link className="btn" to={`/admin/users?q=${encodeURIComponent(n.merchant.name)}`}>
                Open merchant
              </Link>
            )}
            {n.status === 'failed' && (
              <button type="button" className="btn btn--primary" disabled={retry.isPending} onClick={() => retry.mutate()}>
                {retry.isPending ? 'Retrying…' : 'Retry'}
              </button>
            )}
          </>
        )
      }
    >
      {!n ? (
        <RowsSkeleton rows={6} />
      ) : (
        <>
          {n.error && (
            <Notice kind={n.status === 'failed' ? 'error' : 'info'} title={n.status === 'failed' ? 'Failed' : 'Note'}>
              {n.error}
            </Notice>
          )}
          <DrawerSection title="Details">
            <KeyValue
              rows={[
                { label: 'Merchant', value: n.merchant ? `${n.merchant.name} (#${n.merchant.id})` : '—' },
                { label: 'Rule', value: n.rule ? <Link to={`/admin/wallet-alerts/${n.rule.id}`}>{n.rule.name}</Link> : '—' },
                { label: 'Recipient', value: n.recipient || '—' },
                { label: 'Balance at trigger', value: money(n.balance, n.currency) },
                { label: 'Threshold', value: n.threshold ? money(n.threshold, n.currency) : '—' },
                { label: 'Template', value: n.template?.name ?? '—' },
                { label: 'Scheduled', value: dateTime(n.scheduledAt) },
                { label: 'Sent', value: n.sentAt ? dateTime(n.sentAt) : '—' },
                { label: 'Retries', value: n.retryCount },
                { label: 'Provider message ID', value: n.providerMessageId ?? '—' },
                { label: 'Idempotency key', value: <code style={{ fontSize: '.72rem' }}>{n.idempotencyKey}</code> },
              ]}
            />
            <p className="faint" style={{ fontSize: '.78rem', marginTop: 8 }}>
              “Sent” means the provider accepted it. Delivery and read receipts are not reported back by the providers, so they are never shown.
            </p>
          </DrawerSection>
          <DrawerSection title="Rendered message">
            {!rendered ? (
              <span className="faint">Not rendered — the message was never attempted.</span>
            ) : n.channel === 'email' ? (
              <>
                <strong style={{ fontSize: '.85rem' }}>Subject: {String(rendered.subject ?? '')}</strong>
                <iframe title="Rendered email" className="wa-email-frame" sandbox="" style={{ minHeight: 260, marginTop: 8 }} srcDoc={`<!doctype html><meta charset="utf-8"><body style="font-family:sans-serif;padding:16px">${String(rendered.html ?? '')}</body>`} />
              </>
            ) : (
              <>
                <div className="wa-bubble-wrap">
                  <div className="wa-bubble">{String(rendered.preview ?? '')}</div>
                </div>
                <pre className="wa-code" style={{ marginTop: 8 }}>{JSON.stringify({ template: rendered.template, variables: rendered.variables }, null, 2)}</pre>
              </>
            )}
          </DrawerSection>
          <DrawerSection title="Provider response">
            <pre className="wa-code">{n.providerResponse ? JSON.stringify(n.providerResponse, null, 2) : '—'}</pre>
          </DrawerSection>
        </>
      )}
    </Drawer>
  )
}

export function AdminWalletAlertHistoryPage(): React.ReactNode {
  return (
    <>
      <PageHead
        title="Notification History"
        subtitle="Every wallet alert, per channel. Email and WhatsApp are tracked separately."
        crumbs={[{ label: 'Wallet Alerts', to: '/admin/wallet-alerts' }, { label: 'Notification History' }]}
      />
      <Card flush>
        <NotificationTable />
      </Card>
    </>
  )
}
